import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Plus, Trash2, Save, Check, ChevronLeft, RotateCcw, Music, ArrowLeft, Upload, X, Pencil, Zap, RefreshCw, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import WaveLoader from '@/components/WaveLoader';
import { toast } from 'sonner';

// Available tracks to edit subtitles for
const AVAILABLE_TRACKS = [
  { id: 'wealth_activation', name: 'Wealth Activation', file: '/api/uploads/audio/wealth_activation.mp3' },
  { id: 'wealth_activation_daniel', name: 'Wealth Activation (Daniel)', file: '/api/uploads/audio/wealth_activation_daniel.mp3' },
  { id: 'sleep_new', name: 'Sleep', file: '/api/uploads/audio/sleep_new.mp3' },
  { id: 'sleep_ethan', name: 'Deep Sleep (Ethan)', file: '/api/uploads/audio/sleep_ethan.mp3' },
  { id: 'healing_emotional', name: 'Emotional Healing', file: '/api/uploads/audio/healing_emotional.mp3' },
  { id: 'healing_body', name: 'Body Healing', file: '/api/uploads/audio/healing_body.mp3' },
  { id: 'spiritual', name: 'Spiritual Growth', file: '/api/uploads/audio/spiritual.mp3' },
  { id: 'power_warrior', name: 'Unstoppable Warrior', file: '/api/uploads/audio/power_warrior.mp3' },
  { id: 'power_strength', name: 'Strength Affirmations', file: '/api/uploads/audio/power_strength.mp3' },
];

const STORAGE_KEY = 'subtitle_editor_data';
const UPLOADS_KEY = 'subtitle_editor_uploads';
const RENAMES_KEY = 'subtitle_editor_renames';

const getAllSavedData = () => {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : {};
};

const getSavedLines = (trackId) => {
  const all = getAllSavedData();
  return all[trackId]?.lines || [];
};

const saveLines = (trackId, lines) => {
  const all = getAllSavedData();
  all[trackId] = { lines, updatedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
};

const getUploadedTracks = () => {
  const raw = localStorage.getItem(UPLOADS_KEY);
  return raw ? JSON.parse(raw) : [];
};

const saveUploadedTracks = (tracks) => {
  localStorage.setItem(UPLOADS_KEY, JSON.stringify(tracks));
};

const getRenames = () => {
  const raw = localStorage.getItem(RENAMES_KEY);
  return raw ? JSON.parse(raw) : {};
};

const saveRenames = (renames) => {
  localStorage.setItem(RENAMES_KEY, JSON.stringify(renames));
};

const formatTime = (s) => {
  if (s === null || s === undefined) return '0:00.0';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.floor((s % 1) * 10);
  return `${m}:${sec.toString().padStart(2, '0')}.${ms}`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =================== WEALTH TRACK SELECTOR ===================
function WealthTrackSelector() {
  const [audioTracks, setAudioTracks] = useState([]);
  const [currentTrack, setCurrentTrack] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    Promise.all([
      fetch(`${API_URL}/api/admin/audio-tracks`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/settings`).then(r => r.json()),
    ]).then(([tracks, settings]) => {
      setAudioTracks(tracks);
      setCurrentTrack(settings?.wealth_activation_track || '/audio/wealth_activation.mp3');
    }).catch(() => {});
  }, []);

  const handleChange = async (file) => {
    setSaving(true);
    const token = localStorage.getItem('admin_token');
    try {
      await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ wealth_activation_track: file }),
      });
      setCurrentTrack(file);
      toast.success('Wealth Activation track updated!');
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-4 mb-6" data-testid="wealth-track-selector">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-amber-400">Dashboard: Wealth Activation Track</span>
      </div>
      <p className="text-xs text-zinc-500 mb-3">Choose which audio plays when users click Wealth Activation on the Dashboard.</p>
      <select
        value={currentTrack}
        onChange={(e) => handleChange(e.target.value)}
        disabled={saving}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none appearance-none cursor-pointer"
        data-testid="wealth-track-select"
      >
        {audioTracks.map((t) => (
          <option key={t.file} value={t.file}>{t.name}</option>
        ))}
      </select>
    </div>
  );
}

// =================== TRACK SELECTOR ===================
function TrackSelector({ onSelect, onLogout, admin }) {
  const allData = getAllSavedData();
  const fileInputRef = useRef(null);
  const [uploadedTracks, setUploadedTracks] = useState(() => getUploadedTracks());
  const [backendSubtitles, setBackendSubtitles] = useState({});
  const [dbTracks, setDbTracks] = useState([]);

  // Fetch subtitle counts + all DB tracks on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const fetchData = async () => {
      try {
        const [subsRes, tracksRes] = await Promise.all([
          fetch(`${API_URL}/api/subtitles/all`),
          token ? fetch(`${API_URL}/api/admin/tracks`, { headers: { Authorization: `Bearer ${token}` } }) : Promise.resolve(null),
        ]);
        if (subsRes.ok) {
          const docs = await subsRes.json();
          const map = {};
          for (const doc of docs) {
            map[doc.track_id] = { lines: doc.lines || [], updatedAt: doc.updated_at };
            const shortId = doc.track_id.split('/').pop().replace('.mp3', '');
            if (doc.lines?.length > 0) {
              saveLines(shortId, doc.lines);
              saveLines(doc.track_id, doc.lines);
            }
          }
          setBackendSubtitles(map);
        }
        if (tracksRes?.ok) {
          const tracks = await tracksRes.json();
          setDbTracks(tracks);
        }
      } catch {}
    };
    fetchData();
  }, []);

  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.mp3') && !file.type.startsWith('audio/')) {
      toast.error('Please upload an MP3 audio file');
      return;
    }
    processFile(file);
  };

  const processFile = async (file) => {
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/upload-audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      
      const trackId = `upload_${Date.now()}`;
      const newTrack = {
        id: trackId,
        name: data.name,
        file: data.file,
        isUpload: true,
        uploadedAt: new Date().toISOString(),
      };
      const updated = [...uploadedTracks, newTrack];
      setUploadedTracks(updated);
      saveUploadedTracks(updated);
      toast.success(`Uploaded: ${data.name}`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.mp3') && !file.type.startsWith('audio/')) {
      toast.error('Please upload an MP3 audio file');
      return;
    }
    processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeTrack = (track) => {
    if (!window.confirm(`Delete "${renames[track.id] || track.name}"?`)) return;
    if (track.isUpload) {
      const updated = uploadedTracks.filter(t => t.id !== track.id);
      setUploadedTracks(updated);
      saveUploadedTracks(updated);
    } else {
      // Hide built-in track
      const hidden = JSON.parse(localStorage.getItem('subtitle_editor_hidden') || '[]');
      hidden.push(track.id);
      localStorage.setItem('subtitle_editor_hidden', JSON.stringify(hidden));
      setHiddenTracks(hidden);
    }
    // Remove subtitle data & rename
    const all = getAllSavedData();
    delete all[track.id];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
    const updatedRenames = { ...renames };
    delete updatedRenames[track.id];
    setRenames(updatedRenames);
    saveRenames(updatedRenames);
    toast.success('Track removed');
  };

  const [hiddenTracks, setHiddenTracks] = useState(() => 
    JSON.parse(localStorage.getItem('subtitle_editor_hidden') || '[]')
  );

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const renameInputRef = useRef(null);
  const [renames, setRenames] = useState(() => getRenames());

  const startRename = (e, track) => {
    e.stopPropagation();
    setEditingId(track.id);
    setEditName(renames[track.id] || track.name);
    setTimeout(() => renameInputRef.current?.focus(), 50);
  };

  const confirmRename = (trackId) => {
    if (!editName.trim()) { setEditingId(null); return; }
    const updated = { ...renames, [trackId]: editName.trim() };
    setRenames(updated);
    saveRenames(updated);
    // Also update uploaded tracks name if it's an upload
    const updatedUploads = uploadedTracks.map(t =>
      t.id === trackId ? { ...t, name: editName.trim() } : t
    );
    setUploadedTracks(updatedUploads);
    saveUploadedTracks(updatedUploads);
    setEditingId(null);
    toast.success('Renamed');
  };

  const getTrackName = (track) => renames[track.id] || track.name;

  const replaceInputRef = useRef(null);
  const [replacingTrackId, setReplacingTrackId] = useState(null);

  const startReplace = (e, trackId) => {
    e.stopPropagation();
    setReplacingTrackId(trackId);
    setTimeout(() => replaceInputRef.current?.click(), 50);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replacingTrackId) return;
    if (!file.name.endsWith('.mp3') && !file.type.startsWith('audio/')) {
      toast.error('Please upload an MP3 audio file');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/upload-audio`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();

      // Update uploaded tracks list with new file URL (subtitles stay the same)
      const track = allTracks.find(t => t.id === replacingTrackId);
      if (track?.isUpload) {
        const updatedUploads = uploadedTracks.map(t =>
          t.id === replacingTrackId ? { ...t, file: data.file } : t
        );
        setUploadedTracks(updatedUploads);
        saveUploadedTracks(updatedUploads);
      } else {
        // For built-in tracks, store the override in localStorage
        const overrides = JSON.parse(localStorage.getItem('subtitle_editor_overrides') || '{}');
        overrides[replacingTrackId] = data.file;
        localStorage.setItem('subtitle_editor_overrides', JSON.stringify(overrides));
        setTrackOverrides(overrides);
      }

      toast.success(`Audio replaced for "${renames[replacingTrackId] || track?.name}". Subtitles preserved.`);
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      setReplacingTrackId(null);
      if (replaceInputRef.current) replaceInputRef.current.value = '';
    }
  };

  const [trackOverrides, setTrackOverrides] = useState(() =>
    JSON.parse(localStorage.getItem('subtitle_editor_overrides') || '{}')
  );

  // Merge: uploaded tracks + DB tracks (from admin upload) + built-in tracks
  const builtInIds = new Set(AVAILABLE_TRACKS.map(t => t.file));
  const dbTrackItems = dbTracks
    .filter(t => !builtInIds.has(t.audio_url))
    .map(t => ({
      id: t.id,
      name: t.title,
      file: t.audio_url,
      isDb: true,
      category: t.category,
    }));

  const allTracks = [
    ...uploadedTracks.map(t => ({ ...t, isUpload: true })),
    ...dbTrackItems,
    ...AVAILABLE_TRACKS.filter(t => !hiddenTracks.includes(t.id)).map(t => ({
      ...t,
      file: trackOverrides[t.id] || t.file,
    })),
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <a href="/dashboard" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ChevronLeft className="w-4 h-4" />
            <span className="text-sm">Dashboard</span>
          </a>
          <h1 className="text-xl font-serif text-amber-400">Subtitle Editor</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-500">{admin?.email}</span>
            <button onClick={onLogout} className="text-xs text-zinc-600 hover:text-red-400 transition-colors" data-testid="admin-logout-btn">
              Logout
            </button>
          </div>
        </div>

        <p className="text-zinc-400 text-sm mb-6">Choose a track to edit subtitles, or upload your own MP3.</p>

        {/* Wealth Activation Track Selector */}
        <WealthTrackSelector />

        {/* Upload section */}
        <div
          className={`border-2 border-dashed rounded-xl p-6 mb-6 text-center cursor-pointer transition-colors group ${
            dragOver ? 'border-amber-500 bg-amber-500/5' : 'border-zinc-800 hover:border-amber-500/40'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={handleDrop}
          data-testid="upload-area"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,audio/mpeg,audio/*"
            onChange={handleFileUpload}
            className="hidden"
            data-testid="upload-input"
          />
          <Upload className={`w-8 h-8 mx-auto mb-2 transition-colors ${uploading ? 'text-amber-400 animate-pulse' : 'text-zinc-600 group-hover:text-amber-400'}`} />
          <p className="text-sm text-zinc-500 group-hover:text-zinc-300 transition-colors">
            {uploading ? 'Uploading...' : 'Click to upload an MP3 file'}
          </p>
          <p className="text-xs text-zinc-700 mt-1">or drag and drop</p>
        </div>

        {/* Hidden file input for replacing audio */}
        <input
          ref={replaceInputRef}
          type="file"
          accept=".mp3,audio/mpeg,audio/*"
          onChange={handleReplaceFile}
          className="hidden"
        />

        <div className="space-y-3">
          {allTracks.map((track) => {
            const saved = allData[track.id];
            const backendData = backendSubtitles[track.file];
            const lineCount = saved?.lines?.length || backendData?.lines?.length || 0;
            const lastEdited = saved?.updatedAt || backendData?.updatedAt;

            return (
              <div key={track.id} className="flex items-center gap-2">
                <button
                  onClick={() => onSelect({ ...track, name: getTrackName(track) })}
                  className="flex-1 flex items-center gap-4 bg-zinc-900/60 border border-zinc-800 hover:border-amber-500/40 rounded-xl p-4 transition-all hover:bg-zinc-900 group text-left"
                  data-testid={`track-select-${track.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                    track.isUpload ? 'bg-violet-500/10 group-hover:bg-violet-500/20' : track.isDb ? 'bg-teal-500/10 group-hover:bg-teal-500/20' : 'bg-amber-500/10 group-hover:bg-amber-500/20'
                  }`}>
                    {track.isUpload ? <Upload className="w-5 h-5 text-violet-400" /> : track.isDb ? <Music className="w-5 h-5 text-teal-400" /> : <Music className="w-5 h-5 text-amber-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingId === track.id ? (
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={() => confirmRename(track.id)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(track.id); if (e.key === 'Escape') setEditingId(null); }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-zinc-800 border border-amber-500/50 rounded px-2 py-0.5 text-sm text-white focus:outline-none w-full"
                        data-testid={`rename-input-${track.id}`}
                      />
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-sm font-medium text-white group-hover:text-amber-300 transition-colors">{getTrackName(track)}</h3>
                        <button
                          onClick={(e) => startRename(e, track)}
                          className="p-0.5 text-zinc-700 hover:text-amber-400 transition-colors opacity-0 group-hover:opacity-100"
                          title="Rename"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-zinc-600 truncate">{track.isUpload ? 'Uploaded audio' : track.isDb ? track.category : track.file}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {lineCount > 0 ? (
                      <>
                        <span className="text-xs text-amber-400 font-medium">{lineCount} lines</span>
                        <p className="text-[10px] text-zinc-600">{formatDate(lastEdited)}</p>
                      </>
                    ) : (
                      <span className="text-xs text-zinc-600">No subtitles</span>
                    )}
                  </div>
                </button>
                <button
                  onClick={(e) => startReplace(e, track.id)}
                  className="p-2 text-zinc-700 hover:text-amber-400 transition-colors flex-shrink-0"
                  title="Replace audio (keep subtitles)"
                  data-testid={`replace-track-${track.id}`}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); removeTrack(track); }}
                  className="p-2 text-zinc-700 hover:text-red-400 transition-colors flex-shrink-0"
                  title="Remove track"
                  data-testid={`remove-track-${track.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =================== EDITOR ===================
function Editor({ track, onBack }) {
  const audioRef = useRef(null);
  const textInputRef = useRef(null);
  const replaceAudioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [lines, setLines] = useState(() => getSavedLines(track.id));
  const [newText, setNewText] = useState('');
  const [audioSrc, setAudioSrc] = useState(track.file);
  const [replacing, setReplacing] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkInterval, setBulkInterval] = useState(2);

  // Load from backend on mount, sync localStorage to backend if needed
  useEffect(() => {
    const syncData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subtitles?track_id=${encodeURIComponent(track.file)}`);
        const data = await res.json();
        const localLines = getSavedLines(track.id);
        
        if (data?.lines?.length > 0 && localLines.length === 0) {
          // Backend has data, local doesn't — use backend
          setLines(data.lines);
          saveLines(track.id, data.lines);
        } else if (localLines.length > 0 && (!data?.lines || data.lines.length === 0)) {
          // Local has data, backend doesn't — push to backend
          const token = localStorage.getItem('admin_token');
          if (token) {
            const cleanLines = localLines.map(l => ({ start: l.start, end: l.end !== null ? l.end : (l.start + 4), text: l.text }));
            await fetch(`${API_URL}/api/admin/subtitles`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ track_id: track.file, lines: cleanLines }),
            });
          }
        } else if (data?.lines?.length > 0 && localLines.length > 0) {
          // Both have data — use whichever has more lines (user's latest work)
          if (localLines.length >= data.lines.length) {
            // Push local to backend
            const token = localStorage.getItem('admin_token');
            if (token) {
              const cleanLines = localLines.map(l => ({ start: l.start, end: l.end !== null ? l.end : (l.start + 4), text: l.text }));
              await fetch(`${API_URL}/api/admin/subtitles`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ track_id: track.file, lines: cleanLines }),
              });
            }
          } else {
            setLines(data.lines);
            saveLines(track.id, data.lines);
          }
        }
      } catch {}
    };
    syncData();
  }, [track.id, track.file]);
  const [exported, setExported] = useState('');

  // Auto-save on lines change (localStorage + backend)
  useEffect(() => {
    if (lines.length > 0) {
      saveLines(track.id, lines);
      // Debounced auto-save to backend
      const timer = setTimeout(() => {
        const token = localStorage.getItem('admin_token');
        if (!token) return;
        const cleanLines = lines.map(l => ({ start: l.start, end: l.end !== null ? l.end : (l.start + 4), text: l.text }));
        fetch(`${API_URL}/api/admin/subtitles`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ track_id: track.file, lines: cleanLines }),
        }).catch(() => {});
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [lines, track.id, track.file]);

  // Also save to backend
  const saveToBackend = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const cleanLines = lines.map(l => ({ start: l.start, end: l.end !== null ? l.end : (l.start + 4), text: l.text }));
      // Save using track file path as ID (backend normalizes to canonical format)
      await fetch(`${API_URL}/api/admin/subtitles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ track_id: track.file, lines: cleanLines }),
      });
      toast.success('Saved to server!');
    } catch {
      toast.error('Failed to save to server');
    }
  };

  const handleReplaceAudio = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(true);
    try {
      const token = localStorage.getItem('admin_token');
      // If track has a DB id, use the reupload endpoint
      if (track.isDb && track.id) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/api/admin/tracks/${track.id}/reupload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error('Reupload failed');
        const data = await res.json();
        setAudioSrc(data.audio_url);
        toast.success('Audio replaced! Subtitles preserved.');
      } else {
        // For built-in tracks, upload and update
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_URL}/api/admin/upload-audio`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        if (!res.ok) throw new Error('Upload failed');
        const data = await res.json();
        setAudioSrc(data.file);
        toast.success('Audio replaced!');
      }
    } catch {
      toast.error('Failed to replace audio');
    } finally {
      setReplacing(false);
      if (replaceAudioRef.current) replaceAudioRef.current.value = '';
    }
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch((err) => {
        console.error('Playback error:', err);
        toast.error('Could not play audio');
        setIsPlaying(false);
      });
    }
  };

  const seekTo = (time) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = Math.max(0, Math.min(duration, time));
      setCurrentTime(audio.currentTime);
    }
  };

  const addLine = () => {
    if (!newText.trim()) {
      toast.error('Type or paste the text first');
      textInputRef.current?.focus();
      return;
    }
    const time = parseFloat(currentTime.toFixed(1));
    setLines(prev => {
      const updated = [...prev];
      if (updated.length > 0 && updated[updated.length - 1].end === null) {
        updated[updated.length - 1] = { ...updated[updated.length - 1], end: time };
      }
      return [...updated, { start: time, end: null, text: newText.trim() }];
    });
    setNewText('');
    textInputRef.current?.focus();
  };

  const markEnd = (index) => {
    const time = parseFloat(currentTime.toFixed(1));
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], end: time };
      return updated;
    });
  };

  const deleteLine = (index) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const moveLine = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= lines.length) return;
    setLines(prev => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    setLines(prev => {
      const updated = [...prev];
      const [moved] = updated.splice(dragIndex, 1);
      updated.splice(dropIndex, 0, moved);
      return updated;
    });
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const updateLineText = (index, text) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], text };
      return updated;
    });
  };

  const updateLineTime = (index, field, value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: parseFloat(num.toFixed(1)) };
      return updated;
    });
  };

  const clearAll = () => {
    if (lines.length > 0 && !window.confirm('Clear all lines for this track?')) return;
    setLines([]);
    saveLines(track.id, []);
    toast.success('Cleared');
  };

  const handleBulkImport = () => {
    const textLines = bulkText.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    if (textLines.length === 0) {
      toast.error('No lines to import');
      return;
    }
    const startOffset = lines.length > 0 ? (lines[lines.length - 1].end || lines[lines.length - 1].start + 2) : 0;
    const newLines = textLines.map((text, i) => ({
      start: parseFloat((startOffset + i * bulkInterval).toFixed(1)),
      end: null,
      text,
    }));
    setLines(prev => [...prev, ...newLines]);
    setBulkText('');
    setShowBulkImport(false);
    toast.success(`Imported ${textLines.length} lines. Play audio and click "Mark" to set end times.`);
  };

  const exportCode = () => {
    const filled = lines.map((line, i) => {
      const start = line.start;
      let end = line.end;
      if (end === null && i < lines.length - 1) { end = lines[i + 1].start; }
      else if (end === null) { end = start + 4; }
      return { start, end, text: line.text };
    });
    const codeLines = filled.map((t) => {
      const escaped = t.text.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `  { start: ${t.start}, end: ${t.end}, text: "${escaped}" },`;
    });
    const varName = track.id.replace(/[^a-zA-Z0-9]/g, '_');
    const code = `export const ${varName}Script = [\n${codeLines.join('\n')}\n];\n`;
    setExported(code);
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Copied to clipboard!');
    }).catch(() => {
      toast.success('Exported! Copy from box below.');
    });
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;
  const playingIndex = lines.findIndex(
    (l) => l.end !== null && currentTime >= l.start && currentTime < l.end
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-white pb-48">
      {/* Fixed top: audio player */}
      <div className="sticky top-0 z-50 bg-zinc-900/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">All Tracks</span>
            </button>
            <h1 className="text-lg font-serif text-amber-400">{track.name}</h1>
            <div className="flex items-center gap-2">
              <input ref={replaceAudioRef} type="file" accept=".mp3,.mov,.mp4,.wav,.m4a,audio/*,video/*" className="hidden" onChange={handleReplaceAudio} />
              <Button size="sm" variant="outline" onClick={() => replaceAudioRef.current?.click()} disabled={replacing}
                className="text-zinc-400 border-zinc-700 hover:border-amber-500 hover:text-amber-400"
                data-testid="replace-audio-btn">
                <RefreshCw className={`w-3 h-3 mr-1 ${replacing ? 'animate-spin' : ''}`} /> {replacing ? 'Uploading...' : 'Replace Audio'}
              </Button>
              <Button size="sm" variant="outline" onClick={clearAll} className="text-zinc-400 border-zinc-700 hover:border-red-500 hover:text-red-400">
                <RotateCcw className="w-3 h-3 mr-1" /> Clear
              </Button>
              <Button size="sm" variant="outline" onClick={saveToBackend} className="text-zinc-400 border-zinc-700 hover:border-green-500 hover:text-green-400">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" onClick={exportCode} className="bg-amber-500 hover:bg-amber-400 text-zinc-900">
                <Check className="w-3 h-3 mr-1" /> Export
              </Button>
            </div>
          </div>

          {/* Use video element to support both audio and video formats (.mp3, .mov, .mp4, etc.) */}
          <video
            ref={audioRef}
            src={audioSrc}
            onTimeUpdate={() => { if (audioRef.current) setCurrentTime(audioRef.current.currentTime); }}
            onLoadedMetadata={() => { if (audioRef.current) setDuration(audioRef.current.duration); }}
            onEnded={() => setIsPlaying(false)}
            onError={(e) => { 
              console.error('Audio error:', e.target.error);
              toast.error('Audio failed to load. Try re-uploading the file.');
              setIsPlaying(false);
            }}
            preload="metadata"
            playsInline
            style={{position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none'}}
          />

          <div className="flex items-center gap-3">
            <button onClick={() => seekTo(currentTime - 3)} className="text-zinc-400 hover:text-white" title="Back 3s">
              <SkipBack className="w-4 h-4" />
            </button>
            <button
              onClick={togglePlay}
              className="w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-400 flex items-center justify-center transition-all flex-shrink-0"
              data-testid="editor-play-btn"
            >
              {isPlaying
                ? <Pause className="w-5 h-5 text-zinc-900" fill="currentColor" />
                : <Play className="w-5 h-5 text-zinc-900 ml-0.5" fill="currentColor" />
              }
            </button>
            <button onClick={() => seekTo(currentTime + 3)} className="text-zinc-400 hover:text-white" title="Forward 3s">
              <SkipForward className="w-4 h-4" />
            </button>
            <div className="flex-1 mx-2">
              <div
                className="w-full h-3 bg-white/10 rounded-full cursor-pointer overflow-hidden"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  seekTo(((e.clientX - rect.left) / rect.width) * duration);
                }}
              >
                <div className="h-full bg-amber-400 rounded-full" style={{ width: `${progress}%` }} />
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <span className="text-2xl font-mono text-amber-400 tabular-nums">{formatTime(currentTime)}</span>
              <span className="text-sm text-zinc-600 ml-1">/ {formatTime(duration)}</span>
            </div>
          </div>

          {/* Auto-save indicator */}
          <div className="flex items-center gap-2 mt-2">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="text-[10px] text-zinc-600">Auto-saving to browser &amp; server &middot; {lines.length} lines</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Add new line */}
        <div className="bg-zinc-900/60 border border-amber-500/20 rounded-xl p-4 mb-8 sticky top-[155px] z-40 backdrop-blur-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs text-amber-400 font-medium">Add line at</span>
            <span className="text-sm font-mono text-amber-300 bg-amber-500/10 px-2 py-0.5 rounded">{formatTime(currentTime)}</span>
          </div>
          <div className="flex gap-2">
            <textarea
              ref={textInputRef}
              value={newText}
              onChange={(e) => setNewText(e.target.value)}
              placeholder="Pause audio, then type or paste the dialogue text here..."
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none resize-none"
              rows={2}
              data-testid="new-line-input"
            />
            <Button onClick={addLine} className="bg-amber-500 hover:bg-amber-400 text-zinc-900 px-4 self-end" data-testid="add-line-btn">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          <button
            onClick={() => setShowBulkImport(!showBulkImport)}
            className="text-[11px] text-zinc-500 hover:text-amber-400 mt-2 transition-colors"
            data-testid="bulk-import-toggle"
          >
            {showBulkImport ? 'Cancel bulk import' : 'Bulk import script'}
          </button>
          {showBulkImport && (
            <div className="mt-3 pt-3 border-t border-white/10 space-y-3">
              <p className="text-[11px] text-zinc-500">Paste your full script below. Each line becomes a subtitle entry with a {bulkInterval}s start interval. You can then mark the end times while playing.</p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={"Line 1 of your script\nLine 2 of your script\nLine 3 of your script\n..."}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500 focus:outline-none resize-none"
                rows={6}
                data-testid="bulk-import-textarea"
              />
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <label className="text-[11px] text-zinc-500">Interval (sec)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    step="0.5"
                    value={bulkInterval}
                    onChange={(e) => setBulkInterval(parseFloat(e.target.value) || 2)}
                    className="w-16 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-amber-400 font-mono text-center focus:border-amber-500 focus:outline-none"
                    data-testid="bulk-interval-input"
                  />
                </div>
                <span className="text-[10px] text-zinc-600">{bulkText.split('\n').filter(l => l.trim()).length} lines</span>
                <Button
                  onClick={handleBulkImport}
                  disabled={!bulkText.trim()}
                  className="bg-amber-500 hover:bg-amber-400 text-zinc-900 text-xs px-4"
                  data-testid="bulk-import-btn"
                >
                  Import {bulkText.split('\n').filter(l => l.trim()).length} lines
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Lines */}
        {lines.length === 0 ? (
          <div className="text-center py-16 text-zinc-600">
            <p className="text-lg mb-2">No lines yet</p>
            <p className="text-sm">Play the audio, pause at the right moment, type the text, and click Add</p>
          </div>
        ) : (
          <div className="space-y-2">
            {lines.map((line, i) => {
              const isCurrentlyPlaying = i === playingIndex;
              const isDragging = dragIndex === i;
              const isDragOver = dragOverIndex === i;
              return (
                <div
                  key={i}
                  draggable
                  onDragStart={() => handleDragStart(i)}
                  onDragOver={(e) => handleDragOver(e, i)}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-start gap-2 px-3 py-3 rounded-lg border transition-all ${
                    isDragging ? 'opacity-40 scale-[0.98]' :
                    isDragOver ? 'border-amber-500 bg-amber-500/5' :
                    isCurrentlyPlaying ? 'bg-violet-500/10 border-violet-500/30' : 'bg-zinc-900/40 border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid={`line-${i}`}
                >
                  {/* Drag handle + line number */}
                  <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-1 cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-3.5 h-3.5 text-zinc-700 hover:text-zinc-400 transition-colors" />
                    <span className="text-[10px] text-zinc-600 font-mono">{i + 1}</span>
                  </div>

                  {/* Move up/down */}
                  <div className="flex flex-col gap-0.5 flex-shrink-0 pt-0.5">
                    <button onClick={() => moveLine(i, -1)} disabled={i === 0}
                      className="p-0.5 text-zinc-700 hover:text-amber-400 disabled:opacity-20 transition-colors" title="Move up">
                      <ArrowUp className="w-3 h-3" />
                    </button>
                    <button onClick={() => moveLine(i, 1)} disabled={i === lines.length - 1}
                      className="p-0.5 text-zinc-700 hover:text-amber-400 disabled:opacity-20 transition-colors" title="Move down">
                      <ArrowDown className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-zinc-600 w-6">start</span>
                      <input type="number" step="0.1" value={line.start} onChange={(e) => updateLineTime(i, 'start', e.target.value)}
                        className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-center text-green-400 font-mono focus:border-amber-500 focus:outline-none" />
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-zinc-600 w-6">end</span>
                      {line.end !== null ? (
                        <input type="number" step="0.1" value={line.end} onChange={(e) => updateLineTime(i, 'end', e.target.value)}
                          className="w-16 bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-xs text-center text-amber-400 font-mono focus:border-amber-500 focus:outline-none" />
                      ) : (
                        <button onClick={() => markEnd(i)}
                          className="w-16 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 text-[10px] text-amber-400 hover:bg-amber-500/20 transition-colors">
                          Mark
                        </button>
                      )}
                    </div>
                  </div>
                  <input type="text" value={line.text} onChange={(e) => updateLineText(i, e.target.value)}
                    className="flex-1 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-amber-500 text-sm text-zinc-200 py-1 focus:outline-none" />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => seekTo(line.start)} className="p-1.5 text-zinc-600 hover:text-amber-400 transition-colors" title="Jump to this line">
                      <Play className="w-3 h-3" />
                    </button>
                    <button onClick={() => deleteLine(i)} className="p-1.5 text-zinc-600 hover:text-red-400 transition-colors" title="Delete line">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {exported && (
          <div className="mt-8">
            <h3 className="text-sm text-zinc-400 mb-2">Exported Code (copied to clipboard):</h3>
            <textarea readOnly value={exported}
              className="w-full h-48 bg-zinc-900 border border-zinc-700 rounded-lg p-4 text-xs font-mono text-zinc-300 focus:outline-none" />
          </div>
        )}
      </div>
    </div>
  );
}

// =================== MAIN PAGE ===================
export default function SubtitleEditorPage() {
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [admin, setAdmin] = useState(() => {
    const saved = localStorage.getItem('admin_user');
    const token = localStorage.getItem('admin_token');
    if (saved && token) return JSON.parse(saved);
    return null;
  });
  const [checking, setChecking] = useState(true);

  // Verify token is still valid on mount
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { setChecking(false); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/me`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include'
    })
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => setAdmin(data))
      .catch(() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); setAdmin(null); })
      .finally(() => setChecking(false));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    setAdmin(null);
  };

  if (checking) {
    return <WaveLoader />;
  }

  if (!admin) {
    const AdminLoginPage = require('./AdminLoginPage').default;
    return <AdminLoginPage onLogin={(data) => setAdmin(data)} />;
  }

  if (!selectedTrack) {
    return <TrackSelector onSelect={setSelectedTrack} onLogout={handleLogout} admin={admin} />;
  }

  return <Editor track={selectedTrack} onBack={() => setSelectedTrack(null)} />;
}
